import { useEffect, useRef, useState } from 'react';
import {
  AmbientLight,
  Box3,
  BoxGeometry,
  BufferGeometry,
  CanvasTexture,
  CircleGeometry,
  Color,
  CylinderGeometry,
  DirectionalLight,
  DoubleSide,
  EdgesGeometry,
  FrontSide,
  Group,
  LineBasicMaterial,
  LineLoop,
  LineSegments,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  PerspectiveCamera,
  Plane,
  PlaneGeometry,
  PointLight,
  Raycaster,
  Scene,
  SRGBColorSpace,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { buildFixture, fixtureSizeMm } from './fixtures';
import { calculateSurface } from '../../shared/calculations/layoutEngine';
import {
  FixtureKind,
  ProjectState,
  RoomFixture,
  RoomObject,
  SurfaceId,
  TileCategory,
  TileData,
  TilePlacement,
} from '../../shared/types';

const surfaceIds: SurfaceId[] = ['front', 'left', 'right', 'back', 'floor'];

const loadImage = (source: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error(`Не удалось загрузить текстуру: ${source}`));
  image.src = source;
});

const drawImageCover = (
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  width: number,
  height: number,
) => {
  const sourceRatio = image.naturalWidth / image.naturalHeight;
  const targetRatio = width / height;
  let sourceWidth = image.naturalWidth;
  let sourceHeight = image.naturalHeight;
  let sourceX = 0;
  let sourceY = 0;
  if (sourceRatio > targetRatio) {
    sourceWidth = image.naturalHeight * targetRatio;
    sourceX = (image.naturalWidth - sourceWidth) / 2;
  } else {
    sourceHeight = image.naturalWidth / targetRatio;
    sourceY = (image.naturalHeight - sourceHeight) / 2;
  }
  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    x,
    y,
    width,
    height,
  );
};

const drawPlacement = (
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  material: TileData,
  placement: TilePlacement,
  scale: number,
) => {
  const quarterTurn = material.rotation === 90 || material.rotation === 270;
  const physicalWidth = quarterTurn ? material.tileHeight : material.tileWidth;
  const physicalHeight = quarterTurn ? material.tileWidth : material.tileHeight;
  const physicalX = placement.x - placement.sourceX;
  const physicalY = placement.y - placement.sourceY;
  const centerX = (physicalX + physicalWidth / 2) * scale;
  const centerY = (physicalY + physicalHeight / 2) * scale;
  const opacity = material.variationEnabled
    ? 0.96 + ((placement.row + placement.column) % 3) * 0.015
    : 1;
  context.save();
  if (!placement.diagonal) {
    context.beginPath();
    context.rect(
      placement.x * scale,
      placement.y * scale,
      placement.width * scale,
      placement.height * scale,
    );
    context.clip();
  }
  context.globalAlpha = opacity;
  context.translate(centerX, centerY);
  context.rotate((placement.rotation) * Math.PI / 180);
  const mirrored = Boolean(material.mirror) !== Boolean(placement.mirror);
  context.scale(mirrored ? -1 : 1, placement.mirrorY ? -1 : 1);
  drawImageCover(
    context,
    image,
    -material.tileWidth * scale / 2,
    -material.tileHeight * scale / 2,
    material.tileWidth * scale,
    material.tileHeight * scale,
  );
  context.restore();
};

const createSurfaceTexture = async (
  project: ProjectState,
  surfaceId: SurfaceId,
): Promise<CanvasTexture> => {
  const calculation = calculateSurface(project, surfaceId);
  const pixelScale = Math.min(1280 / calculation.width, 1280 / calculation.height);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(64, Math.round(calculation.width * pixelScale));
  canvas.height = Math.max(64, Math.round(calculation.height * pixelScale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas недоступен');
  context.fillStyle = project.grout.color;
  context.fillRect(0, 0, canvas.width, canvas.height);

  const categories = [...new Set(calculation.tiles.map((placement) => placement.category))];
  const images = new Map<TileCategory, HTMLImageElement>();
  await Promise.all(categories.map(async (category) => {
    const material = project.tiles[category];
    if (material) images.set(category, await loadImage(material.imagePath));
  }));
  calculation.tiles.forEach((placement) => {
    const material = project.tiles[placement.category];
    const image = images.get(placement.category);
    if (material && image) drawPlacement(context, image, material, placement, pixelScale);
  });

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.minFilter = LinearFilter;
  texture.anisotropy = 8;
  return texture;
};

const kelvinColor = (kelvin: number) => {
  const temperature = kelvin / 100;
  const red = temperature <= 66
    ? 255
    : 329.698727446 * ((temperature - 60) ** -0.1332047592);
  const green = temperature <= 66
    ? 99.4708025861 * Math.log(temperature) - 161.1195681661
    : 288.1221695283 * ((temperature - 60) ** -0.0755148492);
  const blue = temperature >= 66
    ? 255
    : temperature <= 19
      ? 0
      : 138.5177312231 * Math.log(temperature - 10) - 305.044792731;
  const channel = (value: number) => Math.max(0, Math.min(255, value));
  return new Color(
    `rgb(${Math.round(channel(red))}, ${Math.round(channel(green))}, ${Math.round(channel(blue))})`,
  );
};

const addSurface = (
  room: Group,
  texture: CanvasTexture,
  width: number,
  height: number,
  position: Vector3,
  rotation: Vector3,
  glossy: boolean,
) => {
  const geometry = new PlaneGeometry(width, height);
  const material = new MeshStandardMaterial({
    map: texture,
    roughness: glossy ? 0.34 : 0.78,
    metalness: glossy ? 0.05 : 0,
    // Только внутренняя сторона комнаты: плитка не должна быть видна снаружи
    // (сонаружной стороны стен и снизу пола). Нормали поверхностей обращены
    // внутрь (пол — вверх, стены — к центру), поэтому FrontSide держит кафель изнутри.
    side: FrontSide,
  });
  const mesh = new Mesh(geometry, material);
  mesh.position.copy(position);
  mesh.rotation.set(rotation.x, rotation.y, rotation.z);
  mesh.receiveShadow = true;
  room.add(mesh);
};

const objectTransform = (
  object: RoomObject,
  roomWidth: number,
  roomLength: number,
) => {
  const horizontalCenter = (object.x + object.width / 2) / 1000;
  const y = (object.y + object.height / 2) / 1000;
  if (object.surfaceId === 'front') {
    return {
      position: new Vector3(-roomWidth / 2 + horizontalCenter, y, -roomLength / 2 + 0.008),
      rotationY: 0,
    };
  }
  if (object.surfaceId === 'back') {
    return {
      position: new Vector3(roomWidth / 2 - horizontalCenter, y, roomLength / 2 - 0.008),
      rotationY: Math.PI,
    };
  }
  if (object.surfaceId === 'left') {
    return {
      position: new Vector3(-roomWidth / 2 + 0.008, y, roomLength / 2 - horizontalCenter),
      rotationY: Math.PI / 2,
    };
  }
  return {
    position: new Vector3(roomWidth / 2 - 0.008, y, -roomLength / 2 + horizontalCenter),
    rotationY: -Math.PI / 2,
  };
};

const addDoor = (room: Group, object: RoomObject, transform: { position: Vector3; rotationY: number }) => {
  const group = new Group();
  const w = object.width / 1000;
  const h = object.height / 1000;
  // Полотно двери с филёнками
  const canvas = document.createElement('canvas');
  canvas.width = 128; canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#e8e2d8'; ctx.fillRect(0, 0, 128, 256);
    ctx.fillStyle = '#d9d1c4';
    ctx.fillRect(12, 16, 104, 100);   // верхняя филёнка
    ctx.fillRect(12, 136, 104, 104);  // нижняя филёнка
    ctx.strokeStyle = '#c8bfae'; ctx.lineWidth = 4;
    ctx.strokeRect(12, 16, 104, 100); ctx.strokeRect(12, 136, 104, 104);
    ctx.fillStyle = '#b08d57';        // ручка
    ctx.beginPath(); ctx.arc(104, 128, 6, 0, Math.PI * 2); ctx.fill();
  }
  const doorTexture = new CanvasTexture(canvas);
  doorTexture.colorSpace = SRGBColorSpace;
  const leaf = new Mesh(
    new BoxGeometry(w, h, 0.04),
    new MeshStandardMaterial({ map: doorTexture, roughness: 0.6, side: DoubleSide }),
  );
  group.add(leaf);
  // Наличник (рамка вокруг двери)
  const jamb = new Mesh(
    new BoxGeometry(w + 0.1, h + 0.06, 0.03),
    new MeshStandardMaterial({ color: '#cfc7b8', roughness: 0.7 }),
  );
  jamb.position.z = -0.005;
  group.add(jamb);
  group.position.copy(transform.position);
  group.rotation.y = transform.rotationY;
  group.traverse((child) => { child.castShadow = true; });
  room.add(group);
};

const addWindow = (room: Group, object: RoomObject, transform: { position: Vector3; rotationY: number }) => {
  const group = new Group();
  const w = object.width / 1000;
  const h = object.height / 1000;
  // Стекло
  const glass = new Mesh(
    new PlaneGeometry(w, h),
    new MeshStandardMaterial({
      color: '#bfe0ef', emissive: '#cdeaf6', emissiveIntensity: 0.5,
      roughness: 0.12, transparent: true, opacity: 0.55, side: DoubleSide,
    }),
  );
  group.add(glass);
  // Рама: 4 планки + крест
  const frameColor = '#f2efe8';
  const frameMat = new MeshStandardMaterial({ color: frameColor, roughness: 0.5 });
  const bar = 0.05;
  const top = new Mesh(new BoxGeometry(w + bar * 2, bar, 0.06), frameMat);
  top.position.set(0, h / 2 + bar / 2, 0.01);
  const bottom = new Mesh(new BoxGeometry(w + bar * 2, bar, 0.06), frameMat);
  bottom.position.set(0, -h / 2 - bar / 2, 0.01);
  const left = new Mesh(new BoxGeometry(bar, h, 0.06), frameMat);
  left.position.set(-w / 2 - bar / 2, 0, 0.01);
  const right = new Mesh(new BoxGeometry(bar, h, 0.06), frameMat);
  right.position.set(w / 2 + bar / 2, 0, 0.01);
  const middle = new Mesh(new BoxGeometry(bar * 0.8, h, 0.05), frameMat);
  middle.position.set(0, 0, 0.012);
  const middleH = new Mesh(new BoxGeometry(w, bar * 0.8, 0.05), frameMat);
  middleH.position.set(0, 0, 0.012);
  group.add(top, bottom, left, right, middle, middleH);
  // Подоконник
  const sill = new Mesh(
    new BoxGeometry(w + 0.14, 0.03, 0.1),
    new MeshStandardMaterial({ color: '#e9e5db', roughness: 0.6 }),
  );
  sill.position.set(0, -h / 2 - bar, 0.03);
  group.add(sill);
  group.position.copy(transform.position);
  group.rotation.y = transform.rotationY;
  group.traverse((child) => { child.castShadow = true; });
  room.add(group);
};

const addRoomObject = (
  room: Group,
  object: RoomObject,
  roomWidth: number,
  roomLength: number,
) => {
  const transform = objectTransform(object, roomWidth, roomLength);
  if (object.type === 'door') {
    addDoor(room, object, transform);
    return;
  }
  if (object.type === 'window') {
    addWindow(room, object, transform);
    return;
  }
  const geometry = new BoxGeometry(object.width / 1000, object.height / 1000, 0.05);
  const material = new MeshStandardMaterial({ color: '#303a3d', roughness: 0.9 });
  const mesh = new Mesh(geometry, material);
  mesh.position.copy(transform.position);
  mesh.rotation.y = transform.rotationY;
  mesh.castShadow = true;
  room.add(mesh);
};

/** Бассейн: борт-сопинг, водная гладь и лесенка */
const addPool = (room: Group, roomWidth: number, roomLength: number) => {
  const pw = roomWidth * 0.7;
  const pl = roomLength * 0.62;
  const coping = 0.35;
  const water = new Mesh(
    new PlaneGeometry(pw, pl),
    new MeshStandardMaterial({
      color: '#3a97cf',
      roughness: 0.06,
      metalness: 0.15,
      transparent: true,
      opacity: 0.86,
    }),
  );
  water.rotation.x = -Math.PI / 2;
  water.position.y = 0.012;
  water.receiveShadow = true;
  room.add(water);

  const copingMat = new MeshStandardMaterial({ color: '#d8d0c2', roughness: 0.55 });
  const strip = (w: number, d: number, x: number, z: number) => {
    const mesh = new Mesh(new BoxGeometry(w, 0.08, d), copingMat);
    mesh.position.set(x, 0.04, z);
    mesh.receiveShadow = true;
    room.add(mesh);
  };
  strip(pw + coping * 2, coping, 0, -pl / 2 - coping / 2);
  strip(pw + coping * 2, coping, 0, pl / 2 + coping / 2);
  strip(coping, pl, -pw / 2 - coping / 2, 0);
  strip(coping, pl, pw / 2 + coping / 2, 0);

  const metalMat = new MeshStandardMaterial({ color: '#cfd3d6', roughness: 0.28, metalness: 0.85 });
  [-1, 1].forEach((side) => {
    const rail = new Mesh(new CylinderGeometry(0.022, 0.022, 0.62, 10), metalMat);
    rail.position.set(side * pw * 0.16, 0.31, pl / 2 - 0.25);
    rail.castShadow = true;
    room.add(rail);
  });
  [0.18, 0.4].forEach((rungY) => {
    const rung = new Mesh(new CylinderGeometry(0.016, 0.016, pw * 0.32, 10), metalMat);
    rung.rotation.z = Math.PI / 2;
    rung.position.set(0, rungY, pl / 2 - 0.25);
    room.add(rung);
  });
};

/** CC0-модели сантехники (poly.pizza, Kenney/CreativeTrio) — заменяют примитивы */
const FIXTURE_MODELS: Record<FixtureKind, string> = {
  bath: '/models/bath.glb',
  toilet: '/models/toilet.glb',
  sink: '/models/sink.glb',
};

/** Целевые габариты (м) для автоподгонки модели: ширина×глубина (по X×Z) */
const FIXTURE_TARGETS: Record<FixtureKind, { w: number; d: number }> = {
  bath: { w: 1.6, d: 0.8 },
  toilet: { w: 0.42, d: 0.62 },
  sink: { w: 0.5, d: 0.45 },
};

const fixtureModelCache = new Map<string, Promise<Group>>();

/** Единый цвет сантехнического фарфора — чтобы все модели были в цвет (CC0-модели разнятся: у унитаза чистый #fff, у ванны/раковины bluish-white 0.94,1,1) */
const PORCELAIN_COLOR = new Color(0.94, 1, 1);

/** Приводит материалы всех фикстур к единому «фарфоровому» цвету: near-white, не металл */
const normalizeFixtureMaterials = (model: Group) => {
  model.traverse((child) => {
    if (!(child instanceof Mesh)) return;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => {
      if (!(material instanceof MeshStandardMaterial)) return;
      const c = material.color;
      if (material.metalness < 0.2 && c.r > 0.88 && c.g > 0.88 && c.b > 0.88) {
        material.color.copy(PORCELAIN_COLOR);
      }
    });
  });
};

const loadFixtureModel = (kind: FixtureKind): Promise<Group> => {
  if (!fixtureModelCache.has(kind)) {
    fixtureModelCache.set(kind, new Promise((resolve, reject) => {
      new GLTFLoader().load(
        FIXTURE_MODELS[kind],
        (gltf) => resolve(gltf.scene),
        undefined,
        (error) => { fixtureModelCache.delete(kind); reject(error); },
      );
    }));
  }
  return fixtureModelCache.get(kind)!;
};

/** Ставит модель на пол (minY=0), центрирует по X/Z, масштабирует длину до целевой */
const fitFixtureModel = (model: Group, target: { w: number; d: number }): Group => {
  const box = new Box3().setFromObject(model);
  const size = new Vector3();
  box.getSize(size);
  const long = Math.max(size.x, size.z) || 0.001;
  const targetLong = Math.max(target.w, target.d);
  model.scale.setScalar(targetLong / long);
  box.setFromObject(model);
  const center = new Vector3();
  box.getCenter(center);
  model.position.x -= center.x;
  model.position.z -= center.z;
  // Ставим на пол (minY=0): модели CC0 сидят на разных локальных базовых высотах —
  // подъём на высоту/2 (как в старом варианте) давал «ванну в воздухе» и «утонувшую раковину»
  box.setFromObject(model);
  model.position.y -= box.min.y;
  // Убедиться, что длинная сторона модели ложится вдоль главной оси таргета
  box.setFromObject(model);
  box.getSize(size);
  const modelLongAxis = size.x >= size.z ? 'x' : 'z';
  const targetLongAxis = target.w >= target.d ? 'x' : 'z';
  if (modelLongAxis !== targetLongAxis) model.rotation.y += Math.PI / 2;
  return model;
};

/** Русские подписи сантехники для подсказок в 3D */
const FIXTURE_LABELS: Record<FixtureKind, string> = {
  bath: 'Ванна',
  toilet: 'Унитаз',
  sink: 'Раковина',
};

/** Магнит к стенам (мм) и шаг позиционирования по полу (мм) */
const WALL_SNAP_MM = 160;
const GRID_MM = 10;

interface FixtureHandle {
  id: string;
  kind: FixtureKind;
  rotation: 0 | 90 | 180 | 270;
  x: number;
  z: number;
  group: Group;
}

/** Полугабариты фикстуры с учётом поворота, мм */
const halfExtentsMm = (kind: FixtureKind, rotation: number) => {
  const size = fixtureSizeMm[kind];
  const rotated = rotation === 90 || rotation === 270;
  return rotated ? { hx: size.d / 2, hz: size.w / 2 } : { hx: size.w / 2, hz: size.d / 2 };
};

/** Зажимает фикстуру в границы комнаты, магнитит к ближайшей стене, иначе — к сетке 10 мм */
const snapToRoom = (
  kind: FixtureKind,
  rotation: number,
  rawX: number,
  rawZ: number,
  roomWidthMm: number,
  roomLengthMm: number,
) => {
  const { hx, hz } = halfExtentsMm(kind, rotation);
  const clampX = (value: number) => Math.min(Math.max(value, hx), Math.max(hx, roomWidthMm - hx));
  const clampZ = (value: number) => Math.min(Math.max(value, hz), Math.max(hz, roomLengthMm - hz));
  let x = clampX(rawX);
  let z = clampZ(rawZ);
  let wall = false;
  const leftGap = x - hx;
  const rightGap = roomWidthMm - hx - x;
  if (Math.min(leftGap, rightGap) <= WALL_SNAP_MM) {
    x = leftGap <= rightGap ? hx : roomWidthMm - hx;
    wall = true;
  } else {
    x = Math.round(x / GRID_MM) * GRID_MM;
  }
  const frontGap = z - hz;
  const backGap = roomLengthMm - hz - z;
  if (Math.min(frontGap, backGap) <= WALL_SNAP_MM) {
    z = frontGap <= backGap ? hz : roomLengthMm - hz;
    wall = true;
  } else {
    z = Math.round(z / GRID_MM) * GRID_MM;
  }
  return { x: clampX(x), z: clampZ(z), wall };
};

/** Маркер выделения: контур + полупрозрачная подложка по габаритам фикстуры */
const createSelectionMarker = () => {
  const group = new Group();
  const outlineMaterial = new LineBasicMaterial({ color: '#2f6fd0', transparent: true, opacity: 0.95 });
  const outline = new LineLoop(
    new BufferGeometry().setFromPoints([
      new Vector3(-0.5, 0, -0.5),
      new Vector3(0.5, 0, -0.5),
      new Vector3(0.5, 0, 0.5),
      new Vector3(-0.5, 0, 0.5),
    ]),
    outlineMaterial,
  );
  const fill = new Mesh(
    new PlaneGeometry(1, 1),
    new MeshBasicMaterial({ color: '#2f6fd0', transparent: true, opacity: 0.14, depthWrite: false, side: DoubleSide }),
  );
  fill.rotation.x = -Math.PI / 2;
  group.add(outline, fill);
  group.visible = false;
  return {
    group,
    setSnapped: (snapped: boolean) => {
      const color = snapped ? '#1f8a5c' : '#2f6fd0';
      outlineMaterial.color.set(color);
      (fill.material as MeshBasicMaterial).color.set(color);
    },
  };
};

interface FixtureInteractionOptions {
  container: HTMLElement;
  camera: PerspectiveCamera;
  controls: OrbitControls;
  handles: FixtureHandle[];
  marker: ReturnType<typeof createSelectionMarker>;
  hint: HTMLElement;
  roomWidthMm: number;
  roomLengthMm: number;
  /** id, который был выделен до пересборки сцены */
  selectionId: string | null;
  onSelectionChange: (id: string | null) => void;
  onCommit: (fixture: RoomFixture) => void;
  onDelete: (id: string) => void;
}

/**
 * Выбор и перетаскивание сантехники в 3D: клик — выделить, тянуть — двигать по полу
 * с магнитом к стенам, двойной клик или R — поворот на 90°, Delete — убрать.
 * Возвращает функцию отписки.
 */
const attachFixtureInteraction = ({
  container,
  camera,
  controls,
  handles,
  marker,
  hint,
  roomWidthMm,
  roomLengthMm,
  selectionId,
  onSelectionChange,
  onCommit,
  onDelete,
}: FixtureInteractionOptions) => {
  const raycaster = new Raycaster();
  const pointer = new Vector2();
  const floorPlane = new Plane(new Vector3(0, 1, 0), 0);
  const hitPoint = new Vector3();
  const projected = new Vector3();
  const committed = new Map(
    handles.map((handle) => [handle.id, `${handle.x}|${handle.z}|${handle.rotation}`]),
  );
  let selected: FixtureHandle | null = null;
  let drag: { handle: FixtureHandle; offsetX: number; offsetZ: number; pointerId: number } | null = null;
  const dragLog: string[] = [];
  const log = (message: string) => {
    dragLog.push(message);
    if (dragLog.length > 300) dragLog.shift();
  };

  const pointFromEvent = (event: { clientX: number; clientY: number }) => {
    const rect = container.getBoundingClientRect();
    pointer.set(
      ((event.clientX - rect.left) / Math.max(1, rect.width)) * 2 - 1,
      -((event.clientY - rect.top) / Math.max(1, rect.height)) * 2 + 1,
    );
  };

  const pick = (event: { clientX: number; clientY: number }): { handle: FixtureHandle; point: Vector3 } | null => {
    if (!handles.length) return null;
    pointFromEvent(event);
    raycaster.setFromCamera(pointer, camera);
    const hits = raycaster.intersectObjects(handles.map((handle) => handle.group), true);
    if (!hits.length) return null;
    let object: Object3D | null = hits[0].object;
    while (object && !object.userData?.fixtureId) object = object.parent;
    const id = object?.userData?.fixtureId as string | undefined;
    const handle = id ? handles.find((item) => item.id === id) ?? null : null;
    return handle ? { handle, point: hits[0].point } : null;
  };

  const updateMarker = (handle: FixtureHandle) => {
    const { hx, hz } = halfExtentsMm(handle.kind, handle.rotation);
    marker.group.position.set(
      (handle.x - roomWidthMm / 2) / 1000,
      0.004,
      (handle.z - roomLengthMm / 2) / 1000,
    );
    marker.group.scale.set((hx * 2 + 120) / 1000, 1, (hz * 2 + 120) / 1000);
  };

  const applyPosition = (handle: FixtureHandle, x: number, z: number) => {
    handle.x = x;
    handle.z = z;
    handle.group.position.set((x - roomWidthMm / 2) / 1000, 0, (z - roomLengthMm / 2) / 1000);
    updateMarker(handle);
  };

  const describe = (handle: FixtureHandle, wall = false) =>
    `${FIXTURE_LABELS[handle.kind]} · по ширине ${Math.round(handle.x)} мм · по длине ${Math.round(handle.z)} мм${wall ? ' · у стены' : ''}`;

  const select = (handle: FixtureHandle | null) => {
    selected = handle;
    onSelectionChange(handle?.id ?? null);
    marker.group.visible = Boolean(handle);
    if (handle) {
      updateMarker(handle);
      hint.textContent = `${describe(handle)} · двойной клик или R — поворот · Delete — убрать`;
      hint.classList.add('is-visible');
    } else {
      hint.textContent = '';
      hint.classList.remove('is-visible');
    }
  };

  const commit = (handle: FixtureHandle) => {
    const rounded = Math.round(handle.x);
    const roundedZ = Math.round(handle.z);
    const key = `${rounded}|${roundedZ}|${handle.rotation}`;
    if (committed.get(handle.id) === key) return;
    committed.set(handle.id, key);
    onCommit({
      id: handle.id,
      kind: handle.kind,
      x: rounded,
      z: roundedZ,
      rotation: handle.rotation,
    });
  };

  const rotate = (handle: FixtureHandle, delta: number) => {
    handle.rotation = (((handle.rotation + delta) % 360) + 360) % 360 as 0 | 90 | 180 | 270;
    handle.group.rotation.y = (handle.rotation * Math.PI) / 180;
    const snapped = snapToRoom(handle.kind, handle.rotation, handle.x, handle.z, roomWidthMm, roomLengthMm);
    applyPosition(handle, snapped.x, snapped.z);
    marker.setSnapped(snapped.wall);
    hint.textContent = `${describe(handle)} · двойной клик или R — поворот · Delete — убрать`;
    commit(handle);
  };

  const onPointerDown = (event: PointerEvent) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const hit = pick(event);
    const handle = hit?.handle ?? null;
    log(`down ${Math.round(event.clientX)},${Math.round(event.clientY)} type=${event.pointerType} id=${event.pointerId} → ${handle ? handle.kind : 'мимо'}`);
    if (!hit || !handle) {
      if (selected) select(null);
      container.style.cursor = '';
      return;
    }
    // Клик по сантехнике — перетаскивание: событие не уходит в OrbitControls
    event.stopPropagation();
    event.preventDefault();
    try {
      controls.enabled = false;
      select(handle);
      pointFromEvent(event);
      raycaster.setFromCamera(pointer, camera);
      // Тянем в горизонтальной плоскости, проходящей через точку захвата — работает при любом ракурсе
      floorPlane.constant = -hit.point.y;
      const floorHit = raycaster.ray.intersectPlane(floorPlane, hitPoint);
      if (!floorHit) {
        log(`intersect=null dir=${raycaster.ray.direction.toArray().map((n) => n.toFixed(2)).join(',')} origin=${raycaster.ray.origin.toArray().map((n) => n.toFixed(2)).join(',')}`);
        controls.enabled = true;
        return;
      }
      const nextDrag = {
        handle,
        offsetX: (handle.x - roomWidthMm / 2) / 1000 - hitPoint.x,
        offsetZ: (handle.z - roomLengthMm / 2) / 1000 - hitPoint.z,
        pointerId: event.pointerId,
      };
      drag = nextDrag;
      container.classList.add('fixture-dragging');
      log(`drag старт ${handle.kind} offset=${nextDrag.offsetX.toFixed(3)},${nextDrag.offsetZ.toFixed(3)}`);
    } catch (error) {
      log('ОШИБКА down: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!drag) {
      if (event.pointerType === 'mouse') container.style.cursor = pick(event) ? 'grab' : '';
      return;
    }
    if (event.pointerId !== drag.pointerId) {
      log(`move пропущен: pointerId ${event.pointerId} != ${drag.pointerId}`);
      return;
    }
    pointFromEvent(event);
    raycaster.setFromCamera(pointer, camera);
    if (!raycaster.ray.intersectPlane(floorPlane, hitPoint)) {
      log('move: луч не попал в пол');
      return;
    }
    const rawX = (hitPoint.x + drag.offsetX) * 1000 + roomWidthMm / 2;
    const rawZ = (hitPoint.z + drag.offsetZ) * 1000 + roomLengthMm / 2;
    const snapped = snapToRoom(drag.handle.kind, drag.handle.rotation, rawX, rawZ, roomWidthMm, roomLengthMm);
    applyPosition(drag.handle, snapped.x, snapped.z);
    marker.setSnapped(snapped.wall);
    hint.textContent = describe(drag.handle, snapped.wall);
    log(`move → ${Math.round(snapped.x)}×${Math.round(snapped.z)}${snapped.wall ? ' у стены' : ''}`);
  };

  const onPointerUp = (event: PointerEvent) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const handle = drag.handle;
    drag = null;
    controls.enabled = true;
    container.classList.remove('fixture-dragging');
    log(`up → commit ${Math.round(handle.x)}×${Math.round(handle.z)} rot=${handle.rotation}`);
    commit(handle);
    hint.textContent = `${describe(handle)} · двойной клик или R — поворот · Delete — убрать`;
  };

  const onDoubleClick = (event: { clientX: number; clientY: number }) => {
    const hit = pick(event);
    if (!hit) return;
    select(hit.handle);
    rotate(hit.handle, 90);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    if (event.key === 'Escape') {
      select(null);
      return;
    }
    if (!selected) return;
    const key = event.key.toLowerCase();
    if (key === 'r' || key === 'к') {
      rotate(selected, event.shiftKey ? -90 : 90);
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      const id = selected.id;
      select(null);
      onDelete(id);
    }
  };

  container.addEventListener('pointerdown', onPointerDown, true);
  window.addEventListener('pointermove', onPointerMove, true);
  window.addEventListener('pointerup', onPointerUp, true);
  window.addEventListener('pointercancel', onPointerUp, true);
  container.addEventListener('dblclick', onDoubleClick);
  window.addEventListener('keydown', onKeyDown);

  // Отладочный хук для e2e: экранные координаты каждой фикстуры
  (window as unknown as { __kafelFixtures?: unknown }).__kafelFixtures = () => {
    const rect = container.getBoundingClientRect();
    return handles.map((handle) => {
      const box = new Box3().setFromObject(handle.group);
      const center = box.getCenter(new Vector3());
      const size = box.getSize(new Vector3());
      projected.copy(center).project(camera);
      return {
        id: handle.id,
        kind: handle.kind,
        x: handle.x,
        z: handle.z,
        rotation: handle.rotation,
        roomWidth: roomWidthMm,
        roomLength: roomLengthMm,
        minY: box.min.y,
        maxY: box.max.y,
        size: { x: size.x, z: size.z, y: size.y },
        clientX: rect.left + ((projected.x + 1) / 2) * rect.width,
        clientY: rect.top + ((1 - projected.y) / 2) * rect.height,
      };
    });
  };
  (window as unknown as { __kafelCamera?: unknown }).__kafelCamera = () => ({
    position: camera.position.toArray(),
    target: controls.target.toArray(),
  });
  (window as unknown as { __kafelDragLog?: unknown }).__kafelDragLog = dragLog;
  // Отладочный хук для e2e: поставить камеру так, чтобы вся комната/сантехника были в кадре
  (window as unknown as { __kafelFrameAll?: unknown }).__kafelFrameAll = (kind?: string) => {
    const target = (kind ? handles.find((item) => item.kind === kind) : undefined) ?? handles[0];
    const anchor = target ? target.group.position : new Vector3(0, 0.3, 0);
    controls.target.set(anchor.x, 0.3, anchor.z);
    camera.position.set(anchor.x + 0.9, 0.6, anchor.z + 1.1);
    controls.update();
  };

  if (selectionId) {
    const restored = handles.find((handle) => handle.id === selectionId) ?? null;
    if (restored) select(restored);
  }

  return () => {
    container.removeEventListener('pointerdown', onPointerDown, true);
    window.removeEventListener('pointermove', onPointerMove, true);
    window.removeEventListener('pointerup', onPointerUp, true);
    window.removeEventListener('pointercancel', onPointerUp, true);
    container.removeEventListener('dblclick', onDoubleClick);
    window.removeEventListener('keydown', onKeyDown);
    container.classList.remove('fixture-dragging');
    container.style.cursor = '';
    controls.enabled = true;
    delete (window as unknown as { __kafelFixtures?: unknown }).__kafelFixtures;
    delete (window as unknown as { __kafelCamera?: unknown }).__kafelCamera;
    delete (window as unknown as { __kafelDragLog?: unknown }).__kafelDragLog;
    delete (window as unknown as { __kafelFrameAll?: unknown }).__kafelFrameAll;
  };
};

const disposeScene = (scene: Scene) => {
  scene.traverse((object) => {
    if (object instanceof Mesh || object instanceof LineSegments) {
      object.geometry.dispose();
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      materials.forEach((material) => {
        if (material instanceof MeshStandardMaterial) material.map?.dispose();
        material.dispose();
      });
    }
  });
};

export const Room3D = ({
  project,
  zoom,
  walkthrough = false,
  onFixtureMove,
  onFixtureDelete,
}: {
  project: ProjectState;
  zoom: number;
  walkthrough?: boolean;
  /** Сантехнику подвинули/повернули в 3D — одна запись в истории на перетаскивание */
  onFixtureMove?: (fixture: RoomFixture) => void;
  onFixtureDelete?: (id: string) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const selectionRef = useRef<string | null>(null);
  const callbacksRef = useRef({ onFixtureMove, onFixtureDelete });
  callbacksRef.current = { onFixtureMove, onFixtureDelete };
  // Камера обзора живёт между пересборками сцены: правка проекта не должна сбрасывать вид
  const cameraRef = useRef<{ key: string; position: [number, number, number]; target: [number, number, number] } | null>(null);
  // Кадры (renderer+canvas) прошлых пересборок держим на экране, пока грузятся новые
  // текстуры — иначе на время загрузки виден белый фон сцены. Освобождаются при подстановке
  // нового кадра или при размонтировании.
  const staleFramesRef = useRef<WebGLRenderer[]>([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let cancelled = false;
    let frame = 0;
    let renderer: WebGLRenderer | null = null;
    let controls: OrbitControls | null = null;
    let camera: PerspectiveCamera | null = null;
    let detachInteraction: (() => void) | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let keyHandler: ((event: KeyboardEvent) => void) | null = null;
    const scene = new Scene();
    scene.background = new Color('#d9d7d0');
    const cameraKey = `${project.room.width}x${project.room.length}x${project.room.height}`;
    // Состояние прогулки (WASD)
    const move = { forward: 0, right: 0 };

    const renderRoom = async () => {
      try {
        // Оверлей «песочные часы»: пока текстур не готово, показываем индикатор загрузки
        // поверх старого кадра (старый canvas остаётся в DOM — см. cleanup ниже).
        const loading = document.createElement('div');
        loading.className = 'room-loading';
        loading.innerHTML = '<div class="room-hourglass" />';
        container.appendChild(loading);
        const textures = await Promise.all(
          surfaceIds.map((surfaceId) => createSurfaceTexture(project, surfaceId)),
        );
        if (cancelled) {
          textures.forEach((texture) => texture.dispose());
          loading.remove();
          return;
        }

        const roomWidth = project.room.width / 1000;
        const roomLength = project.room.length / 1000;
        const roomHeight = project.room.height / 1000;
        const room = new Group();
        const textureBySurface = Object.fromEntries(
          surfaceIds.map((surfaceId, index) => [surfaceId, textures[index]]),
        ) as Record<SurfaceId, CanvasTexture>;
        const glossy = project.grout.finish === 'glossy';

        addSurface(
          room,
          textureBySurface.floor,
          roomWidth,
          roomLength,
          new Vector3(0, 0, 0),
          new Vector3(-Math.PI / 2, 0, 0),
          glossy,
        );
        addSurface(
          room,
          textureBySurface.front,
          roomWidth,
          roomHeight,
          new Vector3(0, roomHeight / 2, -roomLength / 2),
          new Vector3(0, 0, 0),
          glossy,
        );
        addSurface(
          room,
          textureBySurface.back,
          roomWidth,
          roomHeight,
          new Vector3(0, roomHeight / 2, roomLength / 2),
          new Vector3(0, Math.PI, 0),
          glossy,
        );
        addSurface(
          room,
          textureBySurface.left,
          roomLength,
          roomHeight,
          new Vector3(-roomWidth / 2, roomHeight / 2, 0),
          new Vector3(0, Math.PI / 2, 0),
          glossy,
        );
        addSurface(
          room,
          textureBySurface.right,
          roomLength,
          roomHeight,
          new Vector3(roomWidth / 2, roomHeight / 2, 0),
          new Vector3(0, -Math.PI / 2, 0),
          glossy,
        );
        project.objects.forEach((object) =>
          addRoomObject(room, object, roomWidth, roomLength));
        // Сантехника: CC0-модели (GLB), при ошибке загрузки — примитив
        const fixtureGroup = new Group();
        const fixtureHandles: FixtureHandle[] = [];
        if (project.fixtures?.length) {
          const fixtureItems = await Promise.all(project.fixtures.map(async (fixture) => {
            const roomCenter = {
              x: fixture.x - project.room.width / 2,
              z: fixture.z - project.room.length / 2,
            };
            let group: Group;
            try {
              const cached = await loadFixtureModel(fixture.kind);
              const model = cached.clone(true) as Group;
              fitFixtureModel(model, FIXTURE_TARGETS[fixture.kind]);
              normalizeFixtureMaterials(model);
              group = new Group();
              group.add(model);
              group.traverse((child) => { if (child instanceof Mesh) { child.castShadow = true; } });
            } catch {
              // Если модель не загрузилась — примитив
              group = buildFixture({ ...fixture, x: roomCenter.x, z: roomCenter.z }).group;
            }
            group.rotation.y = (fixture.rotation * Math.PI) / 180;
            group.position.set(roomCenter.x / 1000, 0, roomCenter.z / 1000);
            group.userData.fixtureId = fixture.id;
            fixtureHandles.push({
              id: fixture.id,
              kind: fixture.kind,
              rotation: fixture.rotation,
              x: fixture.x,
              z: fixture.z,
              group,
            });
            return group;
          }));
          fixtureItems.forEach((group) => fixtureGroup.add(group));
        }
        room.add(fixtureGroup);
        // Маркер выделения сантехники (лежит на полу под фикстурой)
        const selectionMarker = createSelectionMarker();
        room.add(selectionMarker.group);
        const fixtureHint = document.createElement('div');
        fixtureHint.className = 'fixture-hint';
        container.appendChild(fixtureHint);
        // Спец-геометрия помещения из библиотеки: чаша бассейна
        if (project.presetId === 'pool') {
          addPool(room, roomWidth, roomLength);
        }
        const outline = new LineSegments(
          new EdgesGeometry(new BoxGeometry(roomWidth, roomHeight, roomLength)),
          new LineBasicMaterial({ color: '#707775', transparent: true, opacity: 0.28 }),
        );
        outline.position.y = roomHeight / 2;
        room.add(outline);

        // ===== Натяжной потолок =====
        const ceilingColor = new Color(project.ceiling?.color ?? '#f4f2ee');
        const ceilingGlossy = project.ceiling?.glossy ?? true;
        const ceiling = new Mesh(
          new PlaneGeometry(roomWidth, roomLength),
          new MeshStandardMaterial({
            color: ceilingColor,
            roughness: ceilingGlossy ? 0.18 : 0.85,
            metalness: ceilingGlossy ? 0.08 : 0,
            side: DoubleSide,
          }),
        );
        ceiling.rotation.x = Math.PI / 2;
        ceiling.position.y = roomHeight - 0.005;
        ceiling.receiveShadow = true;
        room.add(ceiling);

        // ===== Точечные светильники (0–4) =====
        const spotlights = project.ceiling?.spotlights ?? 0;
        if (spotlights > 0) {
          const spotPositions: Vector3[] = [];
          const inset = 0.35;
          const xs = [-roomWidth / 2 + inset, roomWidth / 2 - inset];
          const zs = [-roomLength / 2 + inset, roomLength / 2 - inset];
          if (spotlights === 1) {
            spotPositions.push(new Vector3(0, 0, 0));
          } else if (spotlights === 2) {
            spotPositions.push(new Vector3(-roomWidth / 4, 0, 0), new Vector3(roomWidth / 4, 0, 0));
          } else if (spotlights === 3) {
            spotPositions.push(
              new Vector3(0, 0, -roomLength / 4),
              new Vector3(-roomWidth / 4, 0, roomLength / 5),
              new Vector3(roomWidth / 4, 0, roomLength / 5),
            );
          } else {
            spotPositions.push(new Vector3(xs[0], 0, zs[0]), new Vector3(xs[1], 0, zs[0]), new Vector3(xs[0], 0, zs[1]), new Vector3(xs[1], 0, zs[1]));
          }
          spotPositions.forEach((spot) => {
            // Корпус светильника
            const housing = new Mesh(
              new CylinderGeometry(0.055, 0.055, 0.012, 20),
              new MeshStandardMaterial({ color: '#e8e8e6', roughness: 0.4, metalness: 0.3 }),
            );
            housing.position.set(spot.x, roomHeight - 0.008, spot.z);
            room.add(housing);
            // Светящееся кольцо
            const glow = new Mesh(
              new CircleGeometry(0.048, 20),
              new MeshStandardMaterial({
                color: '#ffffff',
                emissive: kelvinColor(project.lighting.temperature),
                emissiveIntensity: 1.6,
                roughness: 1,
              }),
            );
            glow.rotation.x = Math.PI / 2;
            glow.position.set(spot.x, roomHeight - 0.013, spot.z);
            room.add(glow);
            // Реальный источник света для освещения плитки
            const spotLight = new PointLight(
              kelvinColor(project.lighting.temperature),
              Math.max(0.15, project.lighting.intensity / 130),
              Math.max(roomWidth, roomLength) * 0.9,
            );
            spotLight.position.set(spot.x, roomHeight - 0.05, spot.z);
            room.add(spotLight);
          });
        }

        scene.add(room);

        scene.add(new AmbientLight('#ffffff', 1.35));
        const mainLight = new DirectionalLight(
          kelvinColor(project.lighting.temperature),
          Math.max(0.25, project.lighting.intensity / 50),
        );
        const lightPosition = {
          left: new Vector3(-roomWidth, roomHeight, roomLength / 3),
          right: new Vector3(roomWidth, roomHeight, roomLength / 3),
          top: new Vector3(0, roomHeight * 1.4, 0),
          front: new Vector3(0, roomHeight * 0.8, roomLength),
        }[project.lighting.direction];
        mainLight.position.copy(lightPosition);
        mainLight.castShadow = true;
        scene.add(mainLight);

        // Если за время асинхронной сборки сцену успели пересобрать — не создаём
        // «осиротевший» canvas (иначе он останется поверх и зальёт старый кадр белым).
        if (cancelled) {
          container.querySelector('.room-loading')?.remove();
          return;
        }
        renderer = new WebGLRenderer({
          antialias: true,
          alpha: false,
          preserveDrawingBuffer: true,
        });
        renderer.domElement.className = 'room-canvas room-canvas-3d';
        renderer.outputColorSpace = SRGBColorSpace;
        renderer.shadowMap.enabled = true;
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        container.appendChild(renderer.domElement);
        // Новый кадр готов — убираем «песочные часы» и освобождаем старые canvas,
        // которые держали на экране, пока грузились текстуры (иначе был белый экран).
        container.querySelector('.room-loading')?.remove();
        const stale = staleFramesRef.current;
        staleFramesRef.current = [];
        stale.forEach((oldRenderer) => {
          oldRenderer.dispose();
          oldRenderer.domElement.remove();
        });

        const viewCamera = new PerspectiveCamera(
          Math.max(38, Math.min(90, 72 / zoom)),
          1,
          0.02,
          100,
        );
        camera = viewCamera;
        controls = new OrbitControls(viewCamera, renderer.domElement);

        const savedCamera = cameraRef.current;
        const restoreCamera = !walkthrough && savedCamera?.key === cameraKey ? savedCamera : null;
        if (walkthrough) {
          // ===== Режим осмотра изнутри: глаза стоящего человека, взгляд мышью, WASD =====
          const eye = Math.min(1.6, roomHeight * 0.62);
          viewCamera.position.set(0, eye, roomLength / 2 - 0.45);
          controls.target.set(0, eye * 0.98, -roomLength / 2 + 0.4);
          controls.enableDamping = true;
          controls.dampingFactor = 0.1;
          controls.enablePan = false;
          controls.minDistance = 0.01;
          controls.maxDistance = Math.max(roomWidth, roomLength) * 1.6;
          controls.maxPolarAngle = Math.PI * 0.62;
          controls.minPolarAngle = Math.PI * 0.3;
          controls.rotateSpeed = 0.45;
          controls.update();
          // WASD-перемещение
          keyHandler = (event: KeyboardEvent) => {
            const code = event.code;
            if (code === 'KeyW' || code === 'ArrowUp' || code === 'Ц'.toLowerCase()) move.forward = 1;
            else if (code === 'KeyS' || code === 'ArrowDown') move.forward = -1;
            else if (code === 'KeyA' || code === 'ArrowLeft') move.right = -1;
            else if (code === 'KeyD' || code === 'ArrowRight') move.right = 1;
          };
          const keyUpHandler = (event: KeyboardEvent) => {
            const code = event.code;
            if (['KeyW', 'ArrowUp', 'KeyS', 'ArrowDown'].includes(code)) move.forward = 0;
            if (['KeyA', 'ArrowLeft', 'KeyD', 'ArrowRight'].includes(code)) move.right = 0;
          };
          window.addEventListener('keydown', keyHandler);
          window.addEventListener('keyup', keyUpHandler);
          (controls as OrbitControls & { __keyUp?: unknown }).__keyUp = keyUpHandler;
        } else {
          // ===== Обзорный режим (как раньше) =====
          if (restoreCamera) {
            // Камера сохраняется между правками проекта — вид не сбрасывается
            viewCamera.position.set(...restoreCamera.position);
            controls.target.set(...restoreCamera.target);
          } else {
            viewCamera.position.set(0, roomHeight * 0.56, roomLength * 0.36);
            controls.target.set(0, roomHeight * 0.45, -roomLength * 0.08);
          }
          controls.enableDamping = true;
          controls.dampingFactor = 0.08;
          controls.enablePan = true;
          controls.maxDistance = Math.max(0.12, Math.min(roomWidth, roomLength) * 0.46);
          controls.minDistance = Math.min(0.35, controls.maxDistance * 0.3);
          controls.maxPolarAngle = Math.PI * 0.83;
          controls.update();
        }

        // ===== Интерактив: выбор и перетаскивание сантехники =====
        detachInteraction = attachFixtureInteraction({
          container,
          camera: viewCamera,
          controls,
          handles: fixtureHandles,
          marker: selectionMarker,
          hint: fixtureHint,
          roomWidthMm: project.room.width,
          roomLengthMm: project.room.length,
          selectionId: selectionRef.current,
          onSelectionChange: (id) => { selectionRef.current = id; },
          onCommit: (fixture) => callbacksRef.current.onFixtureMove?.(fixture),
          onDelete: (id) => {
            selectionRef.current = null;
            callbacksRef.current.onFixtureDelete?.(id);
          },
        });

        const resize = () => {
          if (!renderer) return;
          const width = Math.max(1, container.clientWidth);
          const height = Math.max(1, container.clientHeight);
          viewCamera.aspect = width / height;
          viewCamera.updateProjectionMatrix();
          renderer.setSize(width, height, false);
        };
        resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(container);
        resize();

        const animate = () => {
          if (cancelled || !renderer || !controls) return;
          // WASD-перемещение в режиме прогулки
          if (walkthrough && (move.forward !== 0 || move.right !== 0)) {
            const speed = 1.2 / 60; // м/кадр ≈ 1.2 м/с
            const direction = new Vector3().subVectors(controls.target, viewCamera.position);
            direction.y = 0;
            direction.normalize();
            const rightVector = new Vector3().crossVectors(direction, new Vector3(0, 1, 0)).normalize();
            const step = new Vector3()
              .addScaledVector(direction, move.forward * speed)
              .addScaledVector(rightVector, move.right * speed);
            const eye = Math.min(1.6, roomHeight * 0.62);
            viewCamera.position.add(step);
            // Не выходим за стены комнаты
            const margin = 0.18;
            viewCamera.position.x = Math.max(-roomWidth / 2 + margin, Math.min(roomWidth / 2 - margin, viewCamera.position.x));
            viewCamera.position.z = Math.max(-roomLength / 2 + margin, Math.min(roomLength / 2 - margin, viewCamera.position.z));
            viewCamera.position.y = eye;
            controls.target.add(step);
          }
          controls.update();
          renderer.render(scene, viewCamera);
          frame = window.requestAnimationFrame(animate);
        };
        animate();
      } catch (roomError) {
        container.querySelector('.room-loading')?.remove();
        setError(roomError instanceof Error ? roomError.message : '3D недоступно');
      }
    };

    void renderRoom();
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      detachInteraction?.();
      // Запоминаем вид обзора, чтобы пересборка сцены не сбрасывала камеру
      if (!walkthrough && camera && controls) {
        cameraRef.current = {
          key: cameraKey,
          position: camera.position.toArray() as [number, number, number],
          target: controls.target.toArray() as [number, number, number],
        };
      }
      if (keyHandler) window.removeEventListener('keydown', keyHandler);
      const keyUp = (controls as (OrbitControls & { __keyUp?: (event: KeyboardEvent) => void }) | null)?.__keyUp;
      if (keyUp) window.removeEventListener('keyup', keyUp);
      resizeObserver?.disconnect();
      controls?.dispose();
      // НЕ удаляем canvas здесь: старый кадр остаётся на экране под «песочными часами»,
      // пока новая сцена не будет готова (освобождается в renderRoom после подстановки нового).
      container.querySelector('.room-loading')?.remove();
      container.querySelector('.fixture-hint')?.remove();
      if (renderer) staleFramesRef.current.push(renderer);
      disposeScene(scene);
    };
  }, [project, zoom, walkthrough]);

  // При настоящем размонтировании (переключение на 2D и т.п.) освобождаем оставшиеся
  // необслуженные старые кадры — отдельный пустой эффект отличается от пересборки.
  useEffect(() => () => {
    staleFramesRef.current.forEach((oldRenderer) => {
      oldRenderer.dispose();
      oldRenderer.domElement.remove();
    });
    staleFramesRef.current = [];
  }, []);

  return (
    <div className="room-stage room-stage-3d">
      <div ref={containerRef} className="room-webgl" />
      {error
        ? <div className="empty-visualizer">{error}</div>
        : <div className="room-badge"><span className="live-dot" /> Тяните — обзор · колесо — масштаб · сантехника — тянуть, R — поворот</div>}
      {walkthrough && !error && (
        <div className="walkthrough-hint">WASD / стрелки — идти · мышь — смотреть · Esc — выйти</div>
      )}
    </div>
  );
};
